import {
  computeBackoffDelayMs,
  RetryWithBackoffOptions,
} from "./retryWithBackoff";
import { sleep } from "./utility";
import { AxleServiceError } from "./axle";

export type UploadFileFailureError =
  | "upload"
  | "connection"
  | "signed-url"
  | "validation"
  | "unexpected";

// Tuned for manual policy document uploads via callAxleService.
export const DEFAULT_UPLOAD_RETRY_OPTIONS: RetryWithBackoffOptions = {
  maxAttempts: 6,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  jitterRatio: 0.25,
  onRetry: (error, attempt, delayMs) => {
    // retryUploadOperation is the only caller of onRetry and always passes an
    // AxleServiceError (never a caught exception), so this cast is safe even
    // though the shared RetryWithBackoffOptions type declares `unknown`.
    const { code } = error as AxleServiceError;
    console.log(`[upload-retry] attempt=${attempt} code=${code} delayMs=${delayMs}`);
  },
};

// callAxleService sets code=null when the request never got an HTTP status (e.g.
// connection drop); treat that as transient. Other errors retry only on
// RETRYABLE_UPLOAD_HTTP_CODES (408, 429, 5xx) — not 4xx client faults.
export const isRetryableUploadError = (error: AxleServiceError): boolean => {
  if (error.code === null) {
    return true;
  }
  const RETRYABLE_UPLOAD_HTTP_CODES = new Set([408, 429, 500, 502, 503, 504]);
  return RETRYABLE_UPLOAD_HTTP_CODES.has(error.code);
};

// Retries callAxleService [data, error] uploads without throwing: returns
// [data, null] on success or the last [data, error] when attempts are exhausted
// or isRetryableUploadError is false. Logs once via console.error on terminal
// failure so it trips the same alarm a throw used to.
export const retryUploadOperation = async <T>(
  fn: () => Promise<[T, AxleServiceError | null]>,
  options?: Partial<RetryWithBackoffOptions>
): Promise<[T, AxleServiceError | null]> => {
  const mergedOptions: RetryWithBackoffOptions = {
    ...DEFAULT_UPLOAD_RETRY_OPTIONS,
    ...options,
  };

  if (mergedOptions.maxAttempts < 1) {
    console.error(
      `[upload-retry] invalid maxAttempts=${mergedOptions.maxAttempts}, defaulting to 1`
    );
  }

  // Ensure maxAttempts is at least 1. 
  const maxAttempts = Math.max(1, mergedOptions.maxAttempts);

  let lastResult: [T, AxleServiceError | null] = [null as T, null];

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const [data, error] = await fn();
    if (error === null) {
      return [data, null];
    }

    lastResult = [data, error];

    if (!isRetryableUploadError(error) || attempt >= maxAttempts) {
      console.error(
        `[upload-retry] giving up after attempt=${attempt}/${maxAttempts} code=${error.code} message=${error.message}`
      );
      return lastResult;
    }

    const delayMs = computeBackoffDelayMs(attempt, mergedOptions);
    mergedOptions.onRetry?.(error, attempt, delayMs);
    await sleep(delayMs);
  }

  return lastResult;
};



export const resolveTerminalHttpFailureType = (
  code: number | null,
  manualDocumentErrorKind: "upload" | "signed-url"
): UploadFileFailureError => {
  return code === 500 ? manualDocumentErrorKind : "connection";
};
